import React, { useMemo } from 'react';
import { Link } from 'react-router-dom';
import { Bar, BarChart, CartesianGrid, Cell, Line, LineChart, Pie, PieChart, ResponsiveContainer, Tooltip as ChartTooltip, XAxis, YAxis } from 'recharts';
import { CalendarCheckIcon, FileTextIcon, HandshakeIcon, PhoneCallIcon, SparklesIcon, TrendingUpIcon, UserPlusIcon, UsersIcon, XCircleIcon } from 'lucide-react';
import { useWorkspace } from '../../contexts/AuthContext';
import { PageHeader } from '../../components/crm/PageHeader';
import { StatCard } from '../../components/crm/StatCard';
import { DemoBadge } from '../../components/crm/DemoBadge';
import { EmptyState } from '../../components/crm/EmptyState';
import { Button } from '../../components/ui/Button';
const CHART_COLORS = ['#3D8BFF', '#6EA8F7', '#0B1F4D', '#F59E0B', '#16A34A', '#DC2626'];
function ChartCard({
  title,
  children
}: {
  title: string;
  children: React.ReactNode;
}) {
  return <section className="rounded-2xl border border-border bg-background p-6 shadow-soft" data-id="element-919" data-mp="pl2sh6phm2ox">
      <h2 className="mb-6 text-sm font-semibold text-foreground" data-id="element-920" data-mp="zdxfigegjhcr">{title}</h2>
      <div className="h-64 w-full" data-id="element-921" data-mp="13eg503svobbs">{children}</div>
    </section>;
}
export function Dashboard() {
  const {
    leads,
    stages,
    landings,
    forms,
    isDemo
  } = useWorkspace();

  /**
   * Las métricas se calculan a partir de los estados configurados por la
   * empresa: si se renombran o se crean nuevos, el dashboard se adapta solo.
   */
  const stats = useMemo(() => {
    const initial = stages.find(s => s.isInitial) ?? stages[0];
    const advanced = leads.filter(l => l.stageId !== initial?.id).length;
    return {
      total: leads.length,
      nuevos: leads.filter(l => l.stageId === initial?.id).length,
      initialName: initial?.name ?? 'Nuevo',
      byStage: stages.filter(s => s.active && s.id !== initial?.id).map(stage => ({
        stage,
        count: leads.filter(l => l.stageId === stage.id).length
      })),
      conversion: leads.length ? `${Math.round(advanced / leads.length * 100)}%` : '0%'
    };
  }, [leads, stages]);
  const byDay = useMemo(() => {
    const days: {
      fecha: string;
      leads: number;
    }[] = [];
    for (let i = 13; i >= 0; i -= 1) {
      const date = new Date(Date.now() - i * 86400000);
      const key = date.toISOString().slice(0, 10);
      days.push({
        fecha: date.toLocaleDateString('es-ES', {
          day: '2-digit',
          month: 'short'
        }),
        leads: leads.filter(l => l.createdAt.slice(0, 10) === key).length
      });
    }
    return days;
  }, [leads]);
  const byWeek = useMemo(() => {
    const weeks: {
      semana: string;
      leads: number;
    }[] = [];
    for (let i = 7; i >= 0; i -= 1) {
      const start = Date.now() - (i + 1) * 7 * 86400000;
      const end = Date.now() - i * 7 * 86400000;
      weeks.push({
        semana: `S-${i}`,
        leads: leads.filter(l => {
          const time = new Date(l.createdAt).getTime();
          return time > start && time <= end;
        }).length
      });
    }
    return weeks;
  }, [leads]);
  const bySource = useMemo(() => {
    const map = new Map<string, number>();
    leads.forEach(l => map.set(l.origin.source || 'directo', (map.get(l.origin.source || 'directo') ?? 0) + 1));
    return [...map.entries()].map(([name, value]) => ({
      name,
      value
    }));
  }, [leads]);
  const byLanding = useMemo(() => landings.map(landing => ({
    name: landing.name.length > 18 ? `${landing.name.slice(0, 18)}…` : landing.name,
    leads: leads.filter(l => l.origin.landingId === landing.id).length
  })), [landings, leads]);
  const byForm = useMemo(() => forms.map(form => ({
    name: form.name.length > 18 ? `${form.name.slice(0, 18)}…` : form.name,
    leads: leads.filter(l => l.origin.formId === form.id).length
  })), [forms, leads]);
  const byStage = useMemo(() => stages.map(stage => ({
    name: stage.name,
    leads: leads.filter(l => l.stageId === stage.id).length,
    color: stage.color
  })), [stages, leads]);
  if (leads.length === 0) {
    return <>
        <PageHeader title="Dashboard" description="Resumen de la captación de tu empresa." data-id="element-922" data-mp="1gkly38tdu0i5" />
        <EmptyState icon={<SparklesIcon className="h-5 w-5" data-id="element-924" data-mp="1bomzksecb240" />} title="Todavía no hay datos que mostrar" description="Publica una landing o comparte un formulario para empezar a recibir leads y ver aquí tus métricas." action={<Link to="/app/landings" data-id="element-925" data-mp="11umz53u06l1m">
              <Button data-id="element-926" data-mp="xv5ta9anzlvx">Crear una landing</Button>
            </Link>} data-id="element-923" data-mp="1bn2gbyxh75lg" />
      </>;
  }
  return <>
      <PageHeader title="Dashboard" description="Resumen de la captación de tu empresa." actions={isDemo ? <DemoBadge data-id="element-928" data-mp="nw6j2ma93i6t" /> : undefined} data-id="element-927" data-mp="sv9paa1yvugd" />

      <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4" data-id="element-929" data-mp="nx85fmwpvr3u">
        <StatCard label="Total de leads" value={stats.total} icon={<UsersIcon className="h-4 w-4" data-id="element-931" data-mp="izpwvxmm3dwd" />} data-id="element-930" data-mp="iy52c80aruyi" />
        <StatCard label={stats.initialName} value={stats.nuevos} icon={<UserPlusIcon className="h-4 w-4" data-id="element-933" data-mp="2i8d4edwuzfcu" />} data-id="element-932" data-mp="2iazbkcfu7suj" />
        <StatCard label="Tasa de conversión" value={stats.conversion} icon={<TrendingUpIcon className="h-4 w-4" data-id="element-935" data-mp="2lswrfp2cmm64" />} hint="Leads que han avanzado del primer estado" data-id="element-934" data-mp="2dcphy69rh0b1" />
        {stats.byStage.map(({
        stage,
        count
      }) => <StatCard key={stage.id} label={stage.name} value={count} icon={<PhoneCallIcon className="h-4 w-4" data-id="element-937" data-mp="2ukp1hyngnta6" />} accent={stage.color} data-id="element-936" data-mp="2psff2slk1bcn" />)}
      </div>

      <div className="mt-6 grid gap-5 lg:grid-cols-2" data-id="element-938" data-mp="2zkl1znwy92kz">
        <ChartCard title="Leads por día (últimas 2 semanas)" data-id="element-939" data-mp="2zm5y3hywwtky">
          <ResponsiveContainer width="100%" height="100%" data-id="element-940" data-mp="22uu8ns0e9lgr">
            <LineChart data={byDay} margin={{
            left: -20,
            right: 8,
            top: 8
          }} data-id="element-941" data-mp="26ve8j3ccr36l">
              <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" vertical={false} data-id="element-942" data-mp="2bu7kgrbzqm4n" />
              <XAxis dataKey="fecha" tick={{
              fontSize: 11
            }} stroke="var(--muted-foreground)" data-id="element-943" data-mp="2gu3fw26mh58l" />
              <YAxis allowDecimals={false} tick={{
              fontSize: 11
            }} stroke="var(--muted-foreground)" data-id="element-944" data-mp="3n8kw8xtgzb4b" />
              <ChartTooltip data-id="element-945" data-mp="3s6w5p7furjm0" />
              <Line type="monotone" dataKey="leads" stroke="#3D8BFF" strokeWidth={2} dot={false} data-id="element-946" data-mp="32klc9rkq21dw" />
            </LineChart>
          </ResponsiveContainer>
        </ChartCard>

        <ChartCard title="Leads por semana" data-id="element-947" data-mp="2xnl49b4baok5">
          <ResponsiveContainer width="100%" height="100%" data-id="element-948" data-mp="3cj11tq43p2la">
            <BarChart data={byWeek} margin={{
            left: -20,
            right: 8,
            top: 8
          }} data-id="element-949" data-mp="3cgevftsy4t4n">
              <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" vertical={false} data-id="element-950" data-mp="2jtu2iowto8yz" />
              <XAxis dataKey="semana" tick={{
              fontSize: 11
            }} stroke="var(--muted-foreground)" data-id="element-951" data-mp="2ev92oqlhfrb2" />
              <YAxis allowDecimals={false} tick={{
              fontSize: 11
            }} stroke="var(--muted-foreground)" data-id="element-952" data-mp="2tk578dobym6x" />
              <ChartTooltip data-id="element-953" data-mp="2orlwrpkxw76c" />
              <Bar dataKey="leads" fill="#3D8BFF" radius={[4, 4, 0, 0]} data-id="element-954" data-mp="2orc1pnl1f9ts" />
            </BarChart>
          </ResponsiveContainer>
        </ChartCard>

        <ChartCard title="Leads por fuente" data-id="element-955" data-mp="1w3gauzi5gc4x">
          <ResponsiveContainer width="100%" height="100%" data-id="element-956" data-mp="1w2opehi9nusd">
            <PieChart data-id="element-957" data-mp="bloxjp915k0w">
              <Pie data={bySource} dataKey="value" nameKey="name" innerRadius={50} outerRadius={85} paddingAngle={2} data-id="element-958" data-mp="bl5s380kgo3r">
                {bySource.map((entry, index) => <Cell key={entry.name} fill={CHART_COLORS[index % CHART_COLORS.length]} data-id="element-959" data-mp="1osw23hjty5u" data-mpi={index} />)}
              </Pie>
              <ChartTooltip data-id="element-960" data-mp="6ofcelsobuiv" />
            </PieChart>
          </ResponsiveContainer>
        </ChartCard>

        <ChartCard title="Conversión por estado" data-id="element-961" data-mp="udzy3cg7p5vu">
          <ResponsiveContainer width="100%" height="100%" data-id="element-962" data-mp="ub5efn50vp3h">
            <BarChart data={byStage} layout="vertical" margin={{
            left: 40,
            right: 16
          }} data-id="element-963" data-mp="zaqe5zs0o1h9">
              <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" horizontal={false} data-id="element-964" data-mp="lecuck00fnzm" />
              <XAxis type="number" allowDecimals={false} tick={{
              fontSize: 11
            }} stroke="var(--muted-foreground)" data-id="element-965" data-mp="pc28m0h7xeug" />
              <YAxis type="category" dataKey="name" width={110} tick={{
              fontSize: 11
            }} stroke="var(--muted-foreground)" data-id="element-966" data-mp="3a4m37rpzpxa8" />
              <ChartTooltip data-id="element-967" data-mp="5nkvzgwewj1t" />
              <Bar dataKey="leads" radius={[0, 4, 4, 0]} data-id="element-968" data-mp="n6ktpzikszk">
                {byStage.map(entry => <Cell key={entry.name} fill={entry.color} data-id="element-969" data-mp="pb177elnkgq" />)}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </ChartCard>

        <ChartCard title="Leads por landing" data-id="element-970" data-mp="3nso131mdov28">
          <ResponsiveContainer width="100%" height="100%" data-id="element-971" data-mp="3ntzapi2i8x19">
            <BarChart data={byLanding} margin={{
            left: -20,
            right: 8,
            top: 8
          }} data-id="element-972" data-mp="obpx0gloppwx">
              <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" vertical={false} data-id="element-973" data-mp="kbz5gaoc35rc" />
              <XAxis dataKey="name" tick={{
              fontSize: 11
            }} stroke="var(--muted-foreground)" data-id="element-974" data-mp="fimkqbmrzj3v" />
              <YAxis allowDecimals={false} tick={{
              fontSize: 11
            }} stroke="var(--muted-foreground)" data-id="element-975" data-mp="ajjp1hsoqp8k" />
              <ChartTooltip data-id="element-976" data-mp="139i8mxo41u5u" />
              <Bar dataKey="leads" fill="#0B1F4D" radius={[4, 4, 0, 0]} data-id="element-977" data-mp="1399ul2g94it2" />
            </BarChart>
          </ResponsiveContainer>
        </ChartCard>

        <ChartCard title="Leads por formulario" data-id="element-978" data-mp="1fk9ilduq9rfc">
          <ResponsiveContainer width="100%" height="100%" data-id="element-979" data-mp="1fl2gdpbpl6lc">
            <BarChart data={byForm} margin={{
            left: -20,
            right: 8,
            top: 8
          }} data-id="element-980" data-mp="1kjm51xhoswhe">
              <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" vertical={false} data-id="element-981" data-mp="1ol7mlw561yxx" />
              <XAxis dataKey="name" tick={{
              fontSize: 11
            }} stroke="var(--muted-foreground)" data-id="element-982" data-mp="1tku2yegajvay" />
              <YAxis allowDecimals={false} tick={{
              fontSize: 11
            }} stroke="var(--muted-foreground)" data-id="element-983" data-mp="1yhu7um1ems0h" />
              <ChartTooltip data-id="element-984" data-mp="23byfl7ihv42m" />
              <Bar dataKey="leads" fill="#6EA8F7" radius={[4, 4, 0, 0]} data-id="element-985" data-mp="288ouvsmh5vmz" />
            </BarChart>
          </ResponsiveContainer>
        </ChartCard>
      </div>
    </>;
}